import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { dashboardAPI, notificationAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Building2, 
  DollarSign, 
  TrendingUp, 
  Clock, 
  AlertTriangle,
  Plus,
  ArrowRight,
  Bell,
  CheckCircle2,
  Loader2,
  Calculator,
  Search,
  FileText,
  Users,
  FolderOpen,
  Zap,
  Target,
  PieChart,
  BarChart3,
  Sparkles,
  ChevronRight,
  Home,
  Hammer,
  DollarSign as Dollar,
  TrendingDown,
  Percent
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [greeting, setGreeting] = useState('');

  useEffect(() => {
    fetchDashboard();
    // Set greeting based on time
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Good morning');
    else if (hour < 17) setGreeting('Good afternoon');
    else setGreeting('Good evening');
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await dashboardAPI.getSummary();
      setSummary(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const markNotificationRead = async (id) => {
    try {
      await notificationAPI.markRead(id);
      fetchDashboard();
    } catch (error) {
      console.error('Failed to mark notification read');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      planning: 'status-planning',
      'in-progress': 'status-in-progress',
      completed: 'status-completed',
      sold: 'status-sold',
    };
    return styles[status] || 'bg-secondary text-secondary-foreground';
  };

  // Quick Actions data
  const quickActions = [
    {
      icon: Calculator,
      label: 'Analyze Deal',
      description: 'AI-powered underwriting',
      path: '/underwriting',
      color: 'from-amber-500/20 to-orange-500/20',
      iconColor: 'text-amber-400',
      borderColor: 'hover:border-amber-500/50'
    },
    {
      icon: Home,
      label: 'Property Search',
      description: 'Find & auto-fill property data',
      path: '/property-search',
      color: 'from-indigo-500/20 to-violet-500/20',
      iconColor: 'text-indigo-400',
      borderColor: 'hover:border-indigo-500/50'
    },
    {
      icon: Plus,
      label: 'New Project',
      description: 'Start tracking a flip',
      path: '/projects/new',
      color: 'from-emerald-500/20 to-green-500/20',
      iconColor: 'text-emerald-400',
      borderColor: 'hover:border-emerald-500/50'
    },
    {
      icon: TrendingUp,
      label: 'Financials',
      description: 'ROI & cash flow',
      path: '/financial',
      color: 'from-green-500/20 to-teal-500/20',
      iconColor: 'text-green-400',
      borderColor: 'hover:border-green-500/50'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const totalROI = summary?.potential_profit && summary?.total_invested 
    ? ((summary.potential_profit / summary.total_invested) * 100).toFixed(1) 
    : 0;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Welcome Header with Gradient */}
      <div className="relative overflow-hidden rounded-sm bg-gradient-to-br from-primary/10 via-accent/5 to-transparent border border-border p-6 md:p-8">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="relative z-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-sm text-primary font-medium mb-1">{greeting} 👋</p>
              <h1 className="text-3xl md:text-4xl font-extrabold font-heading tracking-tight">
                {user?.name?.split(' ')[0]}
              </h1>
              <p className="text-muted-foreground mt-2">
                Here's what's happening with your investments today
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button 
                onClick={() => navigate('/underwriting')}
                variant="outline" 
                className="border-primary/50 text-primary hover:bg-primary/10 rounded-sm btn-scale group" 
                data-testid="underwrite-deal-btn"
              >
                <Sparkles className="w-4 h-4 mr-2 group-hover:animate-pulse" />
                AI Underwrite
              </Button>
              <Button 
                onClick={() => navigate('/projects/new')}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm btn-scale shadow-lg shadow-primary/20" 
                data-testid="new-project-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Project
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Grid */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Zap className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-bold font-heading">Quick Actions</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {quickActions.map((action, index) => (
            <Link
              key={action.label}
              to={action.path}
              className={`group relative overflow-hidden rounded-sm border border-border bg-gradient-to-br ${action.color} p-4 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg ${action.borderColor}`}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="flex flex-col h-full">
                <div className={`w-10 h-10 rounded-sm bg-background/50 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                  <action.icon className={`w-5 h-5 ${action.iconColor}`} />
                </div>
                <h3 className="font-semibold text-sm">{action.label}</h3>
                <p className="text-xs text-muted-foreground mt-1">{action.description}</p>
                <ChevronRight className="w-4 h-4 absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground" />
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Stats Grid - Redesigned */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-border bg-card rounded-sm overflow-hidden group hover:border-primary/30 transition-colors" data-testid="stat-card-projects">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Active Deals</p>
                <p className="text-3xl font-bold font-mono text-foreground">{summary?.active_projects || 0}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-green-400" />
                  {summary?.completed_projects || 0} completed
                </p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border bg-card rounded-sm overflow-hidden group hover:border-accent/30 transition-colors" data-testid="stat-card-invested">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Total Invested</p>
                <p className="text-3xl font-bold font-mono text-foreground">{formatCurrency(summary?.total_invested)}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Hammer className="w-3 h-3 text-amber-400" />
                  {formatCurrency(summary?.total_spent)} in rehab
                </p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center group-hover:scale-110 transition-transform">
                <DollarSign className="w-6 h-6 text-accent" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border bg-card rounded-sm overflow-hidden group hover:border-green-500/30 transition-colors" data-testid="stat-card-profit">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Est. Profit</p>
                <p className={`text-3xl font-bold font-mono ${(summary?.potential_profit || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatCurrency(summary?.potential_profit)}
                </p>
                <p className="text-xs flex items-center gap-1">
                  {parseFloat(totalROI) >= 0 ? (
                    <>
                      <TrendingUp className="w-3 h-3 text-green-400" />
                      <span className="text-green-400">{totalROI}% ROI</span>
                    </>
                  ) : (
                    <>
                      <TrendingDown className="w-3 h-3 text-red-400" />
                      <span className="text-red-400">{totalROI}% ROI</span>
                    </>
                  )}
                </p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-green-500/20 to-green-500/5 flex items-center justify-center group-hover:scale-110 transition-transform">
                <TrendingUp className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border bg-card rounded-sm overflow-hidden group hover:border-amber-500/30 transition-colors" data-testid="stat-card-tasks">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Pending Tasks</p>
                <p className="text-3xl font-bold font-mono text-foreground">{summary?.pending_tasks || 0}</p>
                {(summary?.overdue_tasks || 0) > 0 ? (
                  <p className="text-xs text-red-400 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {summary.overdue_tasks} overdue
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3 text-green-400" />
                    On track
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-amber-500/20 to-amber-500/5 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Clock className="w-6 h-6 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Projects - Enhanced */}
        <div className="lg:col-span-2">
          <Card className="border-border bg-card rounded-sm overflow-hidden">
            <CardHeader className="border-b border-border bg-gradient-to-r from-secondary/50 to-transparent">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FolderOpen className="w-5 h-5 text-primary" />
                  <CardTitle className="text-lg font-bold font-heading">Recent Projects</CardTitle>
                </div>
                <Link to="/projects" className="text-sm text-primary hover:underline flex items-center gap-1 group">
                  View all <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {summary?.projects?.length > 0 ? (
                <div className="divide-y divide-border">
                  {summary.projects.map((project, index) => (
                    <Link
                      key={project.id}
                      to={`/projects/${project.id}`}
                      className="block p-4 hover:bg-secondary/30 transition-all duration-200 group"
                      data-testid={`project-item-${project.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                          <Home className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <h3 className="font-semibold truncate group-hover:text-primary transition-colors">{project.name}</h3>
                            <Badge variant="outline" className={`rounded-sm font-mono text-xs uppercase flex-shrink-0 ${getStatusBadge(project.status)}`}>
                              {project.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground truncate mt-1">
                            {project.address}, {project.city}, {project.state}
                          </p>
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />
                              {formatCurrency(project.purchase_price)} purchase
                            </span>
                            <span className="text-xs text-green-400 flex items-center gap-1">
                              <TrendingUp className="w-3 h-3" />
                              {formatCurrency(project.arv)} ARV
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="p-12 text-center">
                  <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mb-4">
                    <Building2 className="w-10 h-10 text-primary/60" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">No projects yet</h3>
                  <p className="text-sm text-muted-foreground mb-6 max-w-xs mx-auto">
                    Start tracking your first fix & flip project and watch your portfolio grow
                  </p>
                  <Button 
                    onClick={() => navigate('/projects/new')}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm shadow-lg shadow-primary/20"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First Project
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Notifications Card */}
          <Card className="border-border bg-card rounded-sm overflow-hidden">
            <CardHeader className="border-b border-border bg-gradient-to-r from-secondary/50 to-transparent">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-primary" />
                  <CardTitle className="text-lg font-bold font-heading">Activity</CardTitle>
                </div>
                {summary?.recent_notifications?.length > 0 && (
                  <Badge className="rounded-full bg-primary/20 text-primary border-0 px-2 py-0.5 text-xs">
                    {summary.recent_notifications.length}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {summary?.recent_notifications?.length > 0 ? (
                <div className="divide-y divide-border max-h-64 overflow-y-auto">
                  {summary.recent_notifications.map((notif) => (
                    <div
                      key={notif.id}
                      className="p-4 hover:bg-secondary/30 transition-colors cursor-pointer"
                      onClick={() => markNotificationRead(notif.id)}
                      data-testid={`notification-${notif.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                          notif.type === 'success' ? 'bg-green-400' :
                          notif.type === 'warning' ? 'bg-amber-400' :
                          notif.type === 'error' ? 'bg-red-400' : 'bg-primary'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{notif.title}</p>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{notif.message}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-green-500/10 flex items-center justify-center mb-3">
                    <CheckCircle2 className="w-6 h-6 text-green-400" />
                  </div>
                  <p className="text-sm font-medium">All caught up!</p>
                  <p className="text-xs text-muted-foreground mt-1">No new notifications</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pro Tips Card */}
          <Card className="border-border bg-gradient-to-br from-primary/10 via-accent/5 to-transparent rounded-sm overflow-hidden">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-sm">Pro Tip</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Use the <span className="text-primary font-medium">AI Underwrite</span> tool to quickly analyze deals. 
                It calculates max offer using the 70% rule and estimates your potential profit.
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/underwriting')}
                className="mt-4 w-full border-primary/30 text-primary hover:bg-primary/10"
              >
                Try AI Analysis
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
